import AppRouter from 'common/AppRouter';
import { TRACKING } from 'common/constants/constants';
import { logOut } from 'common/services/auth.service';
import * as TrackingService from 'common/services/tracking.service';
import { getMarketingCookies } from 'common/utils/cookies.util';
import { getFirstAndLastNames } from 'common/utils/getFirstAndLastNames';
import { isPublicEmail } from 'common/utils/utils';
import Text from 'components/base/Text';
import { Flex } from 'components/base/layout-components';
import RegistrationLayout from 'layouts/RegistrationLayout';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { getAccountByToken, patchAccount } from 'state/callers';
import { myAccountSelector } from 'state/selectors/account';
import {
  toDropDownValues,
  validateFullName
} from './PersonalIdentificationForm.helpers';
import {
  StyledHeader,
  StyledSubHeader
} from './PersonalIdentificationForm.style';
import {
  FORM_VALUES,
  FormData,
  INVALID_ZEROBOUNCE_STATUSES,
  INVALID_ZEROBOUNCE_SUB_STATUSES,
  ROLES,
  SIZES,
  VALIDATION_MESSAGES
} from './consts';
import { useEffect, useState } from 'react';
import { organizationSelector } from 'state/selectors/organization';
import { EMAIL_TYPE, validateEmail } from './zeroBounce.service';
import debounce from 'debounce-promise';
import { Link } from '@linearb/linui-common/marketing/Link';
import { TextField } from '@linearb/linui-common/marketing/TextField';
import { Checkbox } from '@linearb/linui-common/marketing/Checkbox';
import { Button } from '@linearb/linui-common/marketing/Button';
import { Select } from '@linearb/linui-common/marketing/Select';
import { MenuItem } from '@linearb/linui-common/marketing/MenuItem';

/**
 * Personal identification form component
 */
const PersonalIdentificationForm = () => {
  const dispatch = useDispatch();
  const [emailStatus, setEmailStatus] = useState(null);
  const {
    extra = {},
    settings,
    email = '',
    name = '',
    id: accountId,
    organization_id: orgId
  } = useSelector(myAccountSelector) || {};
  const organization = useSelector(organizationSelector);

  const history = useHistory();

  const {
    register,
    handleSubmit,
    control,
    formState: { isValid, errors }
  } = useForm({
    mode: 'all',
    defaultValues: {
      [FORM_VALUES.WORK_EMAIL]: isPublicEmail(email) ? '' : email,
      [FORM_VALUES.FULL_NAME]: validateFullName(name) ? name : undefined, // validation for the default value - name
      [FORM_VALUES.ENABLE_EMAIL_NOTIFICATIONS]: true,
      [FORM_VALUES.ROLE]: undefined,
      [FORM_VALUES.TEAM_SIZE]: undefined
    }
  });

  const initAccount = async () => {
    try {
      await getAccountByToken()(dispatch);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  useEffect(() => {
    if (email === '' || name === '') {
      initAccount();
    }
  }, [email, name]);

  const onSubmit = async (formData: FormData): Promise<void> => {
    const payload = {
      settings: {
        ...(settings || {}),
        notifications: { email: formData.enable_email_notifications }
      },
      extra: {
        ...(extra || {}),
        ...formData
      }
    };

    try {
      await patchAccount(accountId, payload)(dispatch);
      const { firstName, lastName } = getFirstAndLastNames(name);
      const licensePlanName = organization?.license?.plan_name;

      TrackingService.logEvent(TRACKING.EVENTS.SIGN_UP_COMPLETION_OK, {
        identity_email: email,
        identity_git_org: orgId,
        cookies: getMarketingCookies(),
        identity_first_name: firstName,
        identity_last_name: lastName,
        identity_extra_emails: extra?.email,
        form_work_email: formData.work_email,
        form_devs_number: formData.team_size,
        form_role: formData.role,
        form_full_name: formData.full_name,
        form_agree_to_emails: formData.enable_email_notifications,
        license_plan_name: licensePlanName,
        form_email_type: emailStatus.type,
        form_email_status: emailStatus.status
      });
    } catch (e) {
      return e;
    }
    history.push(AppRouter.ONBOARDING.path);
  };

  const handleChangeCredentials = () => {
    logOut(AppRouter.REGISTER.path);

    TrackingService.logEvent(TRACKING.EVENTS.SIGN_UP_IDENTIFICATION_SWITCH, {
      identity_email: email,
      identity_git_org: orgId
    });
  };

  return (
    <RegistrationLayout>
      <Flex direction='column' items='center' width='100%'>
        <StyledHeader>{email}</StyledHeader>
        <Link
          hideChevron
          sx={{ fontSize: 14 }}
          onClick={handleChangeCredentials}
        >
          Use other credentials
        </Link>
        <StyledSubHeader>Tell us more about you:</StyledSubHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex direction='column' gap='1.5rem' width='29rem'>
            <Flex direction='column' position='relative'>
              <TextField
                data-cy='fullName'
                error={!!errors[FORM_VALUES.FULL_NAME]}
                placeholder='Full Name'
                required
                {...register(FORM_VALUES.FULL_NAME, {
                  required: {
                    value: true,
                    message: VALIDATION_MESSAGES[FORM_VALUES.FULL_NAME].required
                  },
                  maxLength: {
                    value: 100,
                    message:
                      VALIDATION_MESSAGES[FORM_VALUES.FULL_NAME].maxLength
                  },
                  validate(fullName) {
                    const isValid = validateFullName(fullName);

                    if (!isValid) {
                      return VALIDATION_MESSAGES[FORM_VALUES.FULL_NAME].pattern;
                    }

                    return true;
                  }
                })}
              />

              {errors[FORM_VALUES.FULL_NAME] && (
                <Text color='radicalRed'>
                  {errors?.[FORM_VALUES.FULL_NAME]?.message as string}
                </Text>
              )}
            </Flex>
            <Flex direction='column' position='relative'>
              <TextField
                data-cy='email'
                error={!!errors[FORM_VALUES.WORK_EMAIL]}
                placeholder='Work Email'
                required
                {...register(FORM_VALUES.WORK_EMAIL, {
                  required: {
                    value: true,
                    message:
                      VALIDATION_MESSAGES[FORM_VALUES.WORK_EMAIL].required
                  },
                  validate: debounce(async (value: string) => {
                    if (!value) {
                      return;
                    }

                    const { free_email, status, sub_status } =
                      await validateEmail(value);

                    setEmailStatus({
                      type: free_email ? EMAIL_TYPE.WORK : EMAIL_TYPE.PRIVATE,
                      status
                    });

                    if (
                      INVALID_ZEROBOUNCE_STATUSES.includes(status) ||
                      INVALID_ZEROBOUNCE_SUB_STATUSES.includes(sub_status)
                    ) {
                      return VALIDATION_MESSAGES[FORM_VALUES.WORK_EMAIL]
                        .validate;
                    }

                    if (free_email) {
                      return VALIDATION_MESSAGES[FORM_VALUES.WORK_EMAIL]
                        .nonWorkEmail;
                    }
                  }, 500)
                })}
              />
              {errors[FORM_VALUES.WORK_EMAIL] && (
                <Text color='radicalRed'>
                  {errors?.[FORM_VALUES.WORK_EMAIL]?.message as string}
                </Text>
              )}
            </Flex>

            <Flex direction='column' position='relative'>
              <Select
                placeholder='Role'
                data-cy='role'
                required
                {...register(FORM_VALUES.ROLE, {
                  required: true
                })}
              >
                {toDropDownValues(ROLES).map(({ value, displayValue }) => (
                  <MenuItem key={value} value={value}>
                    {displayValue}
                  </MenuItem>
                ))}
              </Select>
            </Flex>

            <Select
              placeholder='Total Engineers In Your Company'
              data-cy='size'
              required
              {...register(FORM_VALUES.TEAM_SIZE, {
                required: true
              })}
            >
              {SIZES.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>

            <Flex items='center' self='flex-start'>
              <Controller
                control={control}
                name={FORM_VALUES.ENABLE_EMAIL_NOTIFICATIONS}
                defaultValue={false}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Checkbox
                    checked={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    label={
                      "I'd like to receive emails about news & updates from LinearB."
                    }
                  />
                )}
              />
            </Flex>

            <Flex justify='center' mb='2.2rem'>
              <Button sx={{ width: '250px' }} type='submit' disabled={!isValid}>
                Get Started
              </Button>
            </Flex>
          </Flex>
        </form>
      </Flex>
    </RegistrationLayout>
  );
};

export default PersonalIdentificationForm;
